import React, { useMemo, useState } from 'react';
import { toJsDate } from '../utils/ticketFormatters';
import { validateSupportTicketAttachment } from '../services/supportTicketAttachmentsService';
import { getFileUrl } from '../../../api/files';
import { ListRowSkeletonStack } from '../../../components/loading/skeletons';

function getSupportAgentFirstName(author) {
  const raw = (author?.name || '').trim();
  if (!raw) return 'Support';
  // Legacy: "Billy - Support"
  const dashSplit = raw.split(' - ');
  const base = (dashSplit[0] || raw).trim();
  // If it's an email, keep generic.
  if (base.includes('@')) return 'Support';
  const first = base.split(/\s+/)[0];
  return first || 'Support';
}

export default function SupportTicketThread({ ticket, messages, onSend, sending }) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [openError, setOpenError] = useState('');

  const ticketDisplayNumber = useMemo(() => {
    if (typeof ticket?.ticketNumber === 'number') return `Ticket #${ticket.ticketNumber}`;
    if (typeof ticket?.ticketNumber === 'string' && ticket.ticketNumber.trim()) return `Ticket #${ticket.ticketNumber.trim()}`;
    if (ticket?.id) return `Ticket #${String(ticket.id).slice(0, 6).toUpperCase()}`; // fallback for legacy tickets
    return 'Ticket';
  }, [ticket?.ticketNumber, ticket?.id]);

  const canSend = useMemo(() => {
    return body.trim().length >= 2 && !sending;
  }, [body, sending]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    const text = body.trim();
    await onSend(text, files);
    setBody('');
    setFiles([]);
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;

    // Validate all picked files before accepting
    for (const f of picked) {
      const v = validateSupportTicketAttachment(f);
      if (!v.ok) {
        setFileError(v.error);
        return;
      }
    }
    setFileError('');
    setFiles((prev) => [...prev, ...picked].slice(0, 5));
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const openAttachment = async (attachment) => {
    try {
      setOpenError('');
      const storagePath = attachment?.storagePath;
      if (!storagePath) return;
      const res = await getFileUrl(storagePath);
      const url = res?.url || attachment?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      setOpenError('Could not open attachment.');
    }
  };

  return (
    <div className="st-thread">
      <div className="st-card">
        <div className="st-cardHeader">
          <h3 className="st-cardTitle">{ticket?.subject || 'Ticket'}</h3>
          <span className="st-pill" title={ticket?.id ? `Internal ID: ${ticket.id}` : undefined}>
            {ticketDisplayNumber}
          </span>
        </div>

        {!messages ? (
          <ListRowSkeletonStack rows={4} variant="compact" label="Loading messages" />
        ) : (
          <div className="st-thread">
            {messages.length === 0 ? (
              <div className="st-alert">No messages yet.</div>
            ) : (
              messages.map((m) => {
                const dt = toJsDate(m.createdAt);
                return (
                  <div key={m.id} className="st-message">
                    <div className="st-messageTop">
                      <div className="st-messageAuthor">
                        {m.authorType === 'admin' ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span className="st-messageAuthorName">{getSupportAgentFirstName(m.author)}</span>
                            <span className="st-tag st-tagSupport">Support</span>
                          </span>
                        ) : (
                          (m.author?.name || m.author?.email || 'You')
                        )}
                      </div>
                      <div className="st-messageTime">{dt ? dt.toLocaleString() : '—'}</div>
                    </div>
                    <div className="st-messageBody">{m.body}</div>
                    {Array.isArray(m.attachments) && m.attachments.length > 0 ? (
                      <div className="st-attachments">
                        {m.attachments.map((a, i) => (
                          <button
                            key={`${a.storagePath || a.name || 'file'}-${i}`}
                            type="button"
                            className="st-attachment"
                            onClick={() => openAttachment(a)}
                            title={a.name || 'Attachment'}
                          >
                            <span className="st-attachmentName">{a.name || 'Attachment'}</span>
                            <span className="st-attachmentMeta">{a.contentType || ''}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      <div className="st-card">
        <div className="st-cardHeader">
          <h3 className="st-cardTitle">Reply</h3>
          {sending ? <span className="st-pill">Sending…</span> : null}
        </div>
        <form className="st-form" onSubmit={handleSend}>
          <div className="st-field">
            <label>Attachments</label>
            <div className="st-attachRow">
              <input
                type="file"
                multiple
                onChange={onPickFiles}
                className="st-fileInput"
                accept=".pdf,image/*"
                disabled={sending}
              />
              <span className="st-attachHint">Up to 5 files · 5MB each · PDF/images</span>
            </div>
            {fileError ? <div className="st-alert st-alertError">{fileError}</div> : null}
            {openError ? <div className="st-alert st-alertError">{openError}</div> : null}
            {files.length > 0 ? (
              <div className="st-attachList">
                {files.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="st-attachItem">
                    <span className="st-attachName">{f.name}</span>
                    <button type="button" className="st-attachRemove" onClick={() => removeFile(idx)}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="st-field">
            <label htmlFor="st-reply">Message</label>
            <textarea
              id="st-reply"
              className="st-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your reply…"
              maxLength={4000}
            />
          </div>
          <div className="st-actions">
            <button className="st-btn st-btnPrimary" type="submit" disabled={!canSend}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

